/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { SortOrder, formatDate, sortByProp } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  Button,
  EmptyState,
  EmptyStateProps,
  ICON_FILE_DOWNLOAD,
  ICON_FILE_UPLOAD_WITH_LIST,
  SPINNER_TYPE_EXTRA_SMALL,
  SPINNER_TYPE_REGULAR,
  Spinner,
  Table,
  TableCell,
  TableContent,
  TableProps,
} from '@squiz/sds';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { EntityScvExport, useScvExports } from '../../../../Services/single-customer-view';
import { EXPORTS_DATA_IDS } from '../../Exports.constants';

/**
 * View all exports from the Single Customer View.
 * @export
 * @returns {JSX.Element} The `<ExportList />` component.
 */
export const ExportsList = (): JSX.Element => {
  const { t } = useTranslation();

  const [exports, apiError] = useScvExports();

  // Labels
  const labels = useMemo(() => {
    return {
      created: t(CDP_STRINGS.created),
      download: t(CDP_STRINGS.download),
      finished: t(CDP_STRINGS.finished),
      inProgress: t(CDP_STRINGS.inProgress),
      name: t(CDP_STRINGS.name),
      noExports: t(CDP_STRINGS.noExports),
      status: t(CDP_STRINGS.status),
    };
  }, [t]);

  // Transform all exports into array of table rows
  const tableRows = useMemo(() => {
    if (!exports || exports.data.length === 0) {
      return [];
    }

    const rows = exports.data.map((item: EntityScvExport) => {
      const { exportid, signedURL } = item;

      const name = <span className="customer-exports__file-name">scv-export-{exportid}.csv</span>;

      const dateString = exportid.split('_')[0];
      const createdDate = formatDate(dateString, true);
      const timestamp = new Date(dateString).getTime();

      const status = signedURL ? (
        <span>{labels.finished}</span>
      ) : (
        <div className="customer-exports__loading">
          <Spinner type={SPINNER_TYPE_EXTRA_SMALL} />
          <span>{labels.inProgress}</span>
        </div>
      );

      const downloadFile = (): void => {
        if (signedURL) {
          window.open(signedURL, '_blank', 'noopener');
        }
      };

      const downloadButton = (
        <Button
          data-id={EXPORTS_DATA_IDS.downloadButton}
          disabled={!signedURL}
          format={BUTTON_FORMAT_SOFT}
          icon={ICON_FILE_DOWNLOAD}
          label={labels.download}
          onClick={downloadFile}
          text={labels.download}
          theme={BUTTON_THEME_DEFAULT}
        />
      );

      return {
        createdDate,
        downloadButton,
        name,
        status,
        timestamp,
      };
    });

    return rows.sort((a, b) => sortByProp(a, b, (item) => item.timestamp, SortOrder.Descending));
  }, [exports, labels]);

  if (!exports && !apiError) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  if (tableRows.length === 0 || apiError) {
    const emptyStateProps: EmptyStateProps = {
      caption: labels.noExports,
      dataId: EXPORTS_DATA_IDS.exportsEmptyState,
      svg: ICON_FILE_UPLOAD_WITH_LIST,
    };

    return <EmptyState {...emptyStateProps} />;
  }

  // Table setup
  const tableColumns: Array<TableCell> = [
    {
      content: labels.name,
      key: `name`,
    },
    {
      content: labels.created,
      key: `createdDate`,
    },
    {
      content: labels.status,
      key: `status`,
    },
    {
      content: ``,
      key: `downloadButton`,
    },
  ];

  const tableContent: TableContent = { columns: tableColumns, rows: tableRows };
  const tableProps: TableProps = {
    classes: `customer-exports__table`,
    isPaginated: false,
    tableContent: tableContent,
  };

  return <Table {...tableProps} />;
};
